/* eslint-disable react/require-default-props */
import { Popover } from 'antd';
import moment from 'moment';
import { PureComponent, ReactNode } from 'react';
import { Timestamp } from 'api/models';
import { queueMacrotask } from 'utils/macrotask';
import { uid } from 'utils/uid';
import { DueDateRangePicker } from './range-picker';
import { DueDateSinglePicker } from './single-picker';
import { DueDatePickerTrigger } from './trigger';
import { DateValueType, DueDatePickerButtonProps } from './types';

const DATE_FORMAT = 'DD/MM/YYYY';

export type TimestampValue = Timestamp | null;
type DueDatePickerProps = DueDatePickerButtonProps & {
  canChooseStartDate?: boolean;
  hideFooter?: boolean;
  initialVisibile?: boolean;
  isRangePicker?: boolean;
  placeholder?: string | ReactNode;
  startDate?: TimestampValue;
  dueDate?: TimestampValue;
  overlayClassName?: string;
  renderTrigger?: (handler) => string | ReactNode;
  onDateChange?(values: [TimestampValue, TimestampValue]): void;
  onDatePickerVisibilityChange?: (val: boolean) => void;
};

type DueDatePickerState = {
  datePickerVisibility: boolean;
  isRangePicker: boolean;
  startDate: DateValueType;
  dueDate: DateValueType;
};

/**
 * @deprecated use custom-due-date-picker which handles date in API_DATE_FORMAT
 */
export class DueDatePicker extends PureComponent<
  DueDatePickerProps,
  DueDatePickerState
> {
  singlePopoverRefId: string;

  rangePopoverRefId: string;

  popoverRefId = '';

  constructor(props: DueDatePickerProps) {
    super(props);

    this.rangePopoverRefId = uid('due-date-picker-range');
    this.singlePopoverRefId = uid('due-date-picker-single');
    const {
      startDate,
      dueDate,
      initialVisibile,
      isRangePicker: isRangePickerProps,
    } = props;
    const isRangePicker = isRangePickerProps || !!startDate;

    this.state = {
      datePickerVisibility: !!initialVisibile,
      startDate: startDate ? moment(startDate) : null,
      dueDate: dueDate ? moment(dueDate) : null,
      isRangePicker,
    };

    this.updateDate = this.updateDate.bind(this);
    this.updateDueDate = this.updateDueDate.bind(this);
    this.clearDates = this.clearDates.bind(this);
    this.modifyPickerType = this.modifyPickerType.bind(this);
    this.pickToday = this.pickToday.bind(this);
    this.pickTomorrow = this.pickTomorrow.bind(this);
    this.closeDatePicker = this.closeDatePicker.bind(this);
    this.toggleDatePickerVisibility =
      this.toggleDatePickerVisibility.bind(this);
    this.showDatePicker = this.showDatePicker.bind(this);
    this.popupContainer = this.popupContainer.bind(this);
    this.focusInsideFirstInputField =
      this.focusInsideFirstInputField.bind(this);
    this.setPicker = this.setPicker.bind(this);

    this.setPicker(isRangePicker);
  }

  componentDidUpdate(_, { datePickerVisibility }: DueDatePickerState): void {
    if (!datePickerVisibility) {
      const { startDate } = this.state;
      const { isRangePicker } = this.props;

      this.setPicker(isRangePicker || !!startDate);
    }
  }

  setPicker(isRangePicker: boolean): void {
    this.popoverRefId = isRangePicker
      ? this.rangePopoverRefId
      : this.singlePopoverRefId;

    this.setState({
      isRangePicker,
    });
  }

  focusInsideFirstInputField(): void {
    queueMacrotask(() => {
      const $input = document.querySelector(
        `#${this.popoverRefId} .ant-picker-input input`,
      ) as HTMLInputElement;

      if ($input) {
        $input.focus();
      }
    });
  }

  updateDate(values: [DateValueType, DateValueType]): void {
    const { onDateChange } = this.props;

    if (values == null) {
      this.setState({
        startDate: null,
        dueDate: null,
      });

      if (onDateChange) {
        onDateChange([null, null]);
      }

      return;
    }

    const [updatedStartDate, updatedDueDate] = values;

    this.setState({
      startDate: updatedStartDate,
      dueDate: updatedDueDate,
    });

    if (onDateChange) {
      onDateChange([
        updatedStartDate?.valueOf() || null,
        updatedDueDate?.valueOf() || null,
      ]);
    }
  }

  updateDueDate(date: DateValueType): void {
    this.updateDate([null, date]);
    this.toggleDatePickerVisibility(false);
  }

  clearDates(): void {
    this.updateDate([null, null]);
  }

  modifyPickerType(): void {
    const { isRangePicker } = this.state;

    this.setPicker(!isRangePicker);

    this.focusInsideFirstInputField();
  }

  pickToday(): void {
    this.setState({
      isRangePicker: false,
    });
    this.updateDueDate(moment().endOf('day'));
  }

  pickTomorrow(): void {
    this.setState({
      isRangePicker: false,
    });
    this.updateDueDate(moment().clone().add(1, 'day').endOf('day'));
  }

  toggleDatePickerVisibility(visibility: boolean): void {
    const { onDatePickerVisibilityChange } = this.props;

    if (onDatePickerVisibilityChange) {
      onDatePickerVisibilityChange(visibility);
    }
    this.setState({
      datePickerVisibility: visibility,
    });
  }

  showDatePicker(): void {
    this.toggleDatePickerVisibility(true);

    this.focusInsideFirstInputField();
  }

  closeDatePicker(): void {
    this.toggleDatePickerVisibility(false);
  }

  popupContainer(): HTMLElement {
    return document.getElementById(this.popoverRefId)!;
  }

  render(): ReactNode {
    const { datePickerVisibility, isRangePicker, startDate, dueDate } =
      this.state;

    const {
      canChooseStartDate = true,
      hideFooter,
      className,
      renderTrigger,
      block,
      disabled,
      id,
      placeholder,
      overlayClassName,
      size = 'middle',
    } = this.props;

    const {
      clearDates,
      pickToday,
      pickTomorrow,
      modifyPickerType,
      showDatePicker,
      toggleDatePickerVisibility,
      updateDate,
      updateDueDate,
      popupContainer,
      closeDatePicker,
    } = this;

    const hasValue = startDate || dueDate;

    return (
      <Popover
        id={this.popoverRefId}
        content={
          isRangePicker ? (
            <DueDateRangePicker
              canChooseStartDate={canChooseStartDate}
              getPopupContainer={popupContainer}
              format={DATE_FORMAT}
              value={[startDate, dueDate]}
              updateDateRange={updateDate}
              clearDates={clearDates}
              pickToday={pickToday}
              hideFooter={hideFooter}
              pickTomorrow={pickTomorrow}
              modifyPickerType={modifyPickerType}
              onDone={closeDatePicker}
            />
          ) : (
            <DueDateSinglePicker
              canChooseStartDate={canChooseStartDate}
              getPopupContainer={popupContainer}
              value={dueDate}
              format={DATE_FORMAT}
              updateDates={updateDueDate}
              clearDates={clearDates}
              pickToday={pickToday}
              hideFooter={hideFooter}
              pickTomorrow={pickTomorrow}
              modifyPickerType={modifyPickerType}
              onDone={closeDatePicker}
            />
          )
        }
        placement="bottomLeft"
        trigger="click"
        open={datePickerVisibility}
        onOpenChange={toggleDatePickerVisibility}
        overlayClassName={`due__date__picker ${overlayClassName || ''}`}
      >
        {renderTrigger ? (
          renderTrigger(showDatePicker)
        ) : (
          <DueDatePickerTrigger
            className={className}
            block={block}
            disabled={disabled}
            hasValue={!!hasValue}
            id={id}
            placeholder={placeholder}
            size={size}
            startDate={startDate}
            dueDate={dueDate}
            onClick={showDatePicker}
          />
        )}
      </Popover>
    );
  }
}
